import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import {Tooltip} from "@/components/Common/Tooltip";
import clsxm from "@/lib/clsxm";
import {useSettingsAgencyStore} from "@/store/agency";
import {INFINITY_THRESHOLD} from "@/store/constants";
import usePackageAdminStore from "@/store/packageAdmin";
import {useId, type FC} from "react";
import {useTranslation} from "react-i18next";

const InfiniteLimitSetter: FC<{
  infiniteValue?: number | null;
  isAlwaysVisible?: boolean;
  onChange?: (value: number) => void;
  storeKey?:
    | "buyout"
    | "id"
    | "influencers_per_campaign"
    | "invitations_per_campaign"
    | "number_of_campaigns"
    | "number_of_multiple_active_campaigns"
    | "number_of_seats"
    | "tiktok_visibility_keyword_limit"
    | "user";
  value?: number | null;
}> = ({infiniteValue, isAlwaysVisible, onChange, storeKey, value: valueOverride}) => {
  const {changeValue, modifiedPackage} = usePackageAdminStore();
  const {t} = useTranslation(["common", "plans"]);

  const threshold = infiniteValue === undefined ? INFINITY_THRESHOLD : infiniteValue;
  const value = valueOverride != null ? valueOverride : storeKey ? modifiedPackage[storeKey] : null;
  const isInfinite = threshold === null ? value === null : (typeof value === "number" ? value : threshold) >= threshold;

  const agencyId = useSettingsAgencyStore(state => state.agency.id);
  const isCustomPackage =
    typeof modifiedPackage.template_name !== "undefined" && modifiedPackage.template_name !== null;

  const tooltipId = useId();

  if (!isAlwaysVisible && !isCustomPackage && !agencyId) return null;

  return (
    <>
      <button
        data-tip
        data-tooltip-id={tooltipId}
        onClick={() => {
          if (onChange) {
            onChange(threshold as number);
          } else if (storeKey) {
            changeValue(storeKey, threshold as number);
          }
        }}
        className={clsxm(
          "absolute top-0 right-0 bottom-0 z-0 h-full w-10 rounded-r-md border px-2 transition-all duration-150 hover:border",
          isInfinite
            ? "border-primary-dark-solid bg-primary-dark-solid"
            : "border-neutral-grey-light hover:bg-neutral-white-dark",
          isInfinite ? "cursor-not-allowed" : "",
        )}>
        <InfinityIcon
          className={"h-4 w-6 transition-all duration-150 " + (isInfinite ? "text-white" : "text-neutral-black")}
        />
      </button>
      <Tooltip id={tooltipId} place="bottom" variant="dark" delayShow={750} className="z-50">
        {isInfinite
          ? t("setLimitBack")
          : threshold === null
            ? t("plans:set_limit_to_unlimited")
            : t("setLimitToInfinite", {threshold})}
      </Tooltip>
    </>
  );
};

export default InfiniteLimitSetter;
